import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  FILTER_JOB_KEY,
  FILTER_LOCATION_KEY, FILTER_MANAGER_KEY, FILTER_SHIFT_PATTERN_KEY, FILTER_SKILL_KEY,
  FILTER_WORK_CENTERS_KEY
} from "../../../../ts/assets/constants";

const jobsFilterObject = {
	type: "job",
	jobs: [],
};

const locationsFilterObject = {
	type: "location",
	locations: [],
};

const skillsFilterObject = {
	type: "skill",
	skills: [],
};

const workerTypeFilterObject = {
	type: "workerType",
	workerTypes: [],
};

const workerSubTypeFilterObject = {
	type: "workerSubType",
	workerSubTypes: [],
};

const skillPriorityFilterObject = {
	type: "skillPriority",
	skillPrioritys: [],
};

const departmentFilterObject = {
	type: "department",
	departments: [],
};

const managerFilterObject = {
	type: "manager",
	managers: [],
};

const workCenterFilterObject = {
	type: "workCenter",
	workCenters: [],
};

const shiftPatternFilterObject = {
	type: "shiftPattern",
	shiftPatterns: [],
};

const useCrossSkillFilterController = ({
	filters,
	changeFilters,
	allJobRoles,
	allSkills,
	allWorkCenters, // work center supervisor is associated to
	allShiftPatterns,
	allLocations,
	allManagers,
	workerTypes,
	skillPriorities,
	skillMatrixAppliedFilters,
	skillMatrixFiltersWorkerSubTypes,
  skillMatrixFiltersFilterRelationships,
}) => {
	const [filteredJobs, setFilteredJobs] = useState([]);
	const [filteredLocations, setFilteredLocations] = useState([]);
	const [filteredSkills, setFilteredSkills] = useState([]);
	const [filteredManagers, setFilteredManagers] = useState([]);
	const [filteredWorkcenters, setFilteredWorkcenters] = useState([]);
	const [filteredShiftPatterns, setFilteredShiftPatterns] = useState([]);
	const [allWorkerTypes, setAllWorkerTypes] = useState([]);
	const [allWorkerSubTypes, setAllWorkerSubTypes] = useState([]);
	const [allSkillPriorities, setAllSkillPriorities] = useState([]);
	const [searchedJob, setSearchedJob] = useState("");
	const [searchedWorkcenter, setSearchedWorkcenter] = useState("");
	const [searchedWorkcenters, setSearchedWorkcenters] = useState([]);
	const [searchedLocation, setSearchedLocation] = useState("");
	const [searchedLocations, setSearchedLocations] = useState([]);
	const [searchedJobs, setSearchedJobs] = useState([]);
	const [searchedSkill, setSearchedSkill] = useState("");
	const [searchedSkills, setSearchedSkills] = useState([]);
	const [searchedDepartment, setSearchedDepartment] = useState("");
	const [searchedManager, setSearchedManager] = useState("");
	const [searchedShiftPattern, setSearchedShiftPattern] = useState("");
	const [searchedShiftPatterns, setSearchedShiftPatterns] = useState([]);
	const [expandedSkills, setExpandedSkills] = useState([]);
	const [workcenterExpanded, setWorkcenterExpanded] = useState(false);
	const [jobsExpanded, setJobsExpanded] = useState(true);
	const [locationsExpanded, setLocationsExpanded] = useState(false);
	const [skillsExpanded, setSkillsExpanded] = useState(false);
	const [departmentExpanded, setDepartmentExpanded] = useState(false);
	const [managerExpanded, setManagerExpanded] = useState(false);
	const [workerTypeExpanded, setWorkerTypeExpanded] = useState(false);
	const [workerSubTypeExpanded, setWorkerSubTypeExpanded] = useState(false);
	const [skillPriorityExpanded, setSkillPriorityExpanded] = useState(false);
	const [shiftPatternExpanded, setShiftPatternExpanded] = useState(false);
	const [filterUpdateCount, setFilterUpdateCount] = useState(0);

	const skillListRef = useRef();
	const [rowHeightsForSkill, setRowHeightsForSkill] = useState({});

	const setRowHeightForSkill = (index, skillId, size) => {
		const newRowHeightsForSkill = { ...rowHeightsForSkill };
		newRowHeightsForSkill[skillId] = size;
		setRowHeightsForSkill(newRowHeightsForSkill);
		skillListRef.current.resetAfterIndex(index);
	};

	const getSkillItemSize = useCallback(
		(index, id) => {
			if (index === 0 || index === searchedSkills.length - 1) {
				return rowHeightsForSkill[id] > 42 ? rowHeightsForSkill[id] + 12 : 42; // Height for the first and last rows
			}
			return rowHeightsForSkill[id] || 30;
		},
		[rowHeightsForSkill, searchedSkills]
	);

	const getJobItemSize = useCallback(
		(index) => {
			if (index === 0 || index === searchedJobs.length - 1) {
				return 42; // Height for the first and last rows
			}
			return 30;
		},
		[searchedJobs]
	);

	const getLocationItemSize = useCallback(
		(index) => {
			if (index === 0 || index === searchedLocations.length - 1) {
				return 42; // Height for the first and last rows
			}
			return 30;
		},
		[searchedLocations]
	);

	const getWorkcenterItemSize = useCallback(
		(index) => {
			if (index === 0 || index === searchedWorkcenters.length - 1) {
				return 42;
			}
			return 30;
		},
		[searchedWorkcenters]
	);

	const getShiftPatternItemSize = useCallback(
		(index) => {
			if (index === 0 || index === searchedShiftPatterns.length - 1) {
				return 42;
			}
			return 30;
		},
		[searchedShiftPatterns]
	);
	// Pre-select work center of supervisor
	useEffect(() => {
		try {
			let locationObject = {
				...locationsFilterObject,
			};
			const locationFilter = skillMatrixAppliedFilters.locations
				? skillMatrixAppliedFilters.locations.split(",")
				: [];
			if (
				locationFilter.length !== 0 &&
				locationFilter.length !== allLocations.length
			) {
				locationObject.locations = locationFilter.reduce(
					(locations, locationId) => {
						const location = allLocations.find(
							(location) => location.id === parseInt(locationId)
						);
						if (location) {
							locations.push({
								id: location.id,
								name: location.name,
								timeAdded: new Date().getTime(),
							});
						}
						return locations;
					},
					[]
				);
			}

			let workCenterObject = {
				...workCenterFilterObject,
			};
			const workCenterFilter = skillMatrixAppliedFilters.workcenters
				? skillMatrixAppliedFilters.workcenters.split(",")
				: [];
			if (
				workCenterFilter.length !== 0 &&
				workCenterFilter.length !== allWorkCenters.length
			) {
				workCenterObject.workCenters = workCenterFilter.reduce((wcs, wcId) => {
					const wc = allWorkCenters.find((wc) => wc.id === parseInt(wcId));
					if (wc) {
						wcs.push({
							id: wc.id,
							name: wc.name,
							timeAdded: new Date().getTime() + 1,
						});
					}
					return wcs;
				}, []);
			}

			let shiftPatternObject = {
				...shiftPatternFilterObject,
			};
			const shiftPatternFilter = skillMatrixAppliedFilters.shiftPatterns
				? skillMatrixAppliedFilters.shiftPatterns.split(",")
				: [];
			if (
				shiftPatternFilter.length !== 0 &&
				shiftPatternFilter.length !== allShiftPatterns.length
			) {
				shiftPatternObject.workCenters = shiftPatternFilter.reduce(
					(sps, spId) => {
						const sp = allShiftPatterns.find((sp) => sp.id === parseInt(spId));
						if (sp) {
							sps.push({
								id: sp.id,
								name: sp.name,
								timeAdded: new Date().getTime() + 2,
							});
						}
						return sps;
					},
					[]
				);
			}

			let managerObject = {
				...managerFilterObject,
			};
			managerObject.managers = skillMatrixAppliedFilters.managers
				? skillMatrixAppliedFilters.managers
						.split(",")
						.reduce((managers, managerId) => {
							const manager = allManagers.find(
								(manager) => manager.id === parseInt(managerId)
							);
							if (manager) {
								managers.push({
									id: manager.id,
									name: manager.name,
									timeAdded: new Date().getTime() + 3,
								});
							}
							return managers;
						}, [])
				: [];
			changeFilters([locationObject, workCenterObject, managerObject]);
		} catch (e) {
			console.log("Error applying filter", e);
			// Do nothing
		}
	}, [
		changeFilters,
		skillMatrixAppliedFilters,
		allLocations,
		allWorkCenters,
		allManagers,
		allShiftPatterns,
	]);

	useEffect(() => {
		if (workerTypes) {
			setAllWorkerTypes(workerTypes);
		}
	}, [workerTypes]);

	useEffect(() => {
		if (skillMatrixFiltersWorkerSubTypes) {
			setAllWorkerSubTypes(skillMatrixFiltersWorkerSubTypes);
		}
	}, [skillMatrixFiltersWorkerSubTypes]);

	useEffect(() => {
		if (skillPriorities) {
			setAllSkillPriorities(skillPriorities);
		}
	}, [skillPriorities]);

  const createRelationshipMap = (mappings = []) => {
    const relationshipMap = new Map();
    for (const { fromId, toIds } of mappings) {
      if (fromId && toIds) {
        // Using a Set for the 'toIds' allows for O(1) lookups
        relationshipMap.set(fromId, new Set(toIds));
      }
    }
    return relationshipMap;
  };

  // This calculation only runs when skillMatrixFiltersFilterRelationships changes.
  const { locationToWcMap, wcToJobMap, jobToSkillMap, skillToJobMap, wcToShiftPatternMap, wcToManagerMap } = useMemo(() => {
    const findAndCreateMap = (fromKey, toKey) => {
      const relationship = skillMatrixFiltersFilterRelationships.find(
        (rel) => rel.from === fromKey && rel.to === toKey
      );
      // If relationship is not found and reverse is available then create new relationship based on that
      if (!relationship && skillMatrixFiltersFilterRelationships.find((rel) => rel.from === toKey && rel.to === fromKey)) {
        const reverseRelationship = skillMatrixFiltersFilterRelationships.find(
          (rel) => rel.from === toKey && rel.to === fromKey
        );
        // Based on toIds create new set of fromId and form array toIds with all ids
        const fromIdToMap = new Map();
        for (const mapping of reverseRelationship.mappings) {
          const { fromId, toIds } = mapping;
          if (fromId && toIds) {
            for (const toId of toIds) {
              if (!fromIdToMap.has(toId)) {
                fromIdToMap.set(toId, new Set());
              }
              fromIdToMap.get(toId).add(fromId);
            }
          }
        }

        const newMappings = [];
        for (const [toId, fromIds] of fromIdToMap) {
          newMappings.push({
            fromId: toId,
            toIds: Array.from(fromIds),
          });
        }

        return createRelationshipMap(newMappings);
      }
      return createRelationshipMap(relationship?.mappings);
    };

    return {
      locationToWcMap: findAndCreateMap(
        FILTER_LOCATION_KEY,
        FILTER_WORK_CENTERS_KEY
      ),
      wcToJobMap: findAndCreateMap(FILTER_WORK_CENTERS_KEY, FILTER_JOB_KEY),
      jobToSkillMap: findAndCreateMap(FILTER_JOB_KEY, FILTER_SKILL_KEY),
      skillToJobMap: findAndCreateMap(FILTER_SKILL_KEY, FILTER_JOB_KEY),
      wcToShiftPatternMap: findAndCreateMap(FILTER_WORK_CENTERS_KEY, FILTER_SHIFT_PATTERN_KEY),
      wcToManagerMap: findAndCreateMap(FILTER_WORK_CENTERS_KEY, FILTER_MANAGER_KEY),
    };
  }, [skillMatrixFiltersFilterRelationships]);

  // This only recalculates when the main 'filters' array changes.
  const selectedLocationIds = useMemo(() => {
    const locationFilter = filters.find((filter) => filter.type === "location");
    return new Set(locationFilter?.locations?.map((loc) => loc.id) ?? []);
  }, [filters]);

  const selectedWorkCenterIds = useMemo(
    () => new Set(filters.find((filter) => filter.type === "workCenter")?.workCenters?.map((wc) => wc.id) ?? []),
    [filters]
  );

  const selectedJobIds = useMemo(
    () => new Set(filters.find((filter) => filter.type === "job")?.jobs?.map((job) => job.id) ?? []),
    [filters]
  );

  const selectedSkillIds = useMemo(
    () => new Set(filters.find((filter) => filter.type === "skill")?.skills?.map((skill) => skill.id) ?? []),
    [filters]
  );

  // 1. Derivce the list of reduced work centers based on selected locations.
  const reducedAllWorkCenters = useMemo(() => {
    // If no location filter is active, return all work centers.
    if (selectedLocationIds.size === 0) {
      return allWorkCenters;
    }

    const allowedWcIds = new Set();
    // For each selected location, add its corresponding work center IDs to a set.
    for (const locationId of selectedLocationIds) {
      const wcIds = locationToWcMap.get(locationId);
      if (wcIds) {
        for (const wcId of wcIds) {
          allowedWcIds.add(wcId);
        }
      }
    }

    // Return only the work centers whose IDs are in the allowed set.
    return allWorkCenters.filter((wc) => allowedWcIds.has(wc.id));
  }, [allWorkCenters, selectedLocationIds, locationToWcMap]);

  // 2. Derive the list of reduced job roles based on the reduced work centers.
  const reducedAllJobs = useMemo(() => {
    const allJobsArray = Object.values(allJobRoles);
    // If work centers weren't filtered or any workcenter is not selected, return all jobs.
    if (reducedAllWorkCenters.length === allWorkCenters.length
      && selectedWorkCenterIds.size === 0
      && selectedSkillIds.size === 0) {
      return allJobsArray;
    }

    let tempSelectedWorkCenterIds = selectedWorkCenterIds;
    if (tempSelectedWorkCenterIds.size === 0) {
      tempSelectedWorkCenterIds = new Set(reducedAllWorkCenters.map((wc) => wc.id));
    }

    let allowedJobIds = new Set();
    if (tempSelectedWorkCenterIds.size === 0) {
      // For each reduced work center, add its corresponding job IDs to a set.
      for (const wc of reducedAllWorkCenters) {
        const jobIds = wcToJobMap.get(wc.id);
        if (jobIds) {
          for (const jobId of jobIds) {
            allowedJobIds.add(jobId);
          }
        }
      }
    } else {
      // Reduce jobs based on selected work center ids using wcToJobMap
      for (const wcId of tempSelectedWorkCenterIds) {
        const jobIds = wcToJobMap.get(wcId);
        if (jobIds) {
          for (const jobId of jobIds) {
            allowedJobIds.add(jobId);
          }
        }
      }
    }

    if (selectedLocationIds.size === 0 && selectedWorkCenterIds.size === 0 && selectedSkillIds.size !== 0) {
      if (reducedAllWorkCenters === allWorkCenters
        && selectedWorkCenterIds.size === 0) {
        for (const preJob of allJobsArray) {
          allowedJobIds.add(preJob.id);
        }
      }
      let found = false;
      let combinedJobIds = new Set();
      for (const skillId of selectedSkillIds) {
        const jobIds = skillToJobMap.get(skillId);
        if (jobIds && jobIds?.size !== 0) {
          combinedJobIds = new Set([...combinedJobIds, ...jobIds]);
          found = true;
        }
      }
      if (!found) {
        allowedJobIds = new Set();
      } else {
        // Remove other job ids from allowed job Ids which do not exists jobIds
        allowedJobIds = new Set(
          [...allowedJobIds].filter((id) => combinedJobIds.has(id)));
      }
    }
    // Return only the jobs whose IDs are in the allowed set.
    return allJobsArray.filter((job) => allowedJobIds.has(job.id));
  }, [allJobRoles, reducedAllWorkCenters, allWorkCenters, selectedWorkCenterIds, selectedSkillIds, selectedLocationIds.size, wcToJobMap, skillToJobMap]);

  // 3. Derive the list of reduced skills based on the reduced job roles.
  const reducedAllSkills = useMemo(() => {
    const allJobsArray = Object.values(allJobRoles);
    // If jobs weren't filtered or selected, return all skills.
    if ((reducedAllJobs.length === allJobsArray.length && selectedJobIds.size === 0) || (
      selectedLocationIds.size === 0
      && selectedWorkCenterIds.size === 0
      && selectedJobIds.size === 0
      && selectedSkillIds.size !== 0)) {
      return allSkills;
    }

    const allowedSkillIds = new Set();
    if (selectedJobIds.size === 0) {
      // For each reduced job, add its corresponding skill IDs to a set.
      for (const job of reducedAllJobs) {
        const skillIds = jobToSkillMap.get(job.id);
        if (skillIds) {
          for (const skillId of skillIds) {
            allowedSkillIds.add(skillId);
          }
        }
      }
    } else {
      // Reduce skill based on selected job ids using jobToSkillMap
      for (const jobId of selectedJobIds) {
        const skillIds = jobToSkillMap.get(jobId);
        if (skillIds) {
          for (const skillId of skillIds) {
            allowedSkillIds.add(skillId);
          }
        }
      }
    }

    // Return only the skills whose IDs are in the allowed set.
    return allSkills.filter((skill) => allowedSkillIds.has(skill.id));
  }, [allJobRoles, reducedAllJobs, selectedJobIds, selectedLocationIds.size, selectedWorkCenterIds.size, selectedSkillIds.size, allSkills, jobToSkillMap]);

  const reducedShiftPatterns = useMemo(() => {
    // If no location filter is active, return all work centers.
    if (selectedWorkCenterIds.size === 0 && reducedAllWorkCenters.length === allWorkCenters.length) {
      return allShiftPatterns;
    }

    let tempSelectedWorkCenterIds = selectedWorkCenterIds;
    if (tempSelectedWorkCenterIds.size === 0) {
      tempSelectedWorkCenterIds = new Set(reducedAllWorkCenters.map((wc) => wc.id));
    }

    const allowedShiftPatternsIds = new Set();
    // For each selected work center, add its corresponding shift pattern IDs to a set.
    for (const wcId of tempSelectedWorkCenterIds) {
      const shiftPatternIds = wcToShiftPatternMap.get(wcId);
      if (shiftPatternIds) {
        for (const shiftPatternId of shiftPatternIds) {
          allowedShiftPatternsIds.add(shiftPatternId);
        }
      }
    }

    // Return only the shift pattern whose IDs are in the allowed set.
    return allShiftPatterns.filter((shiftPattern) => allowedShiftPatternsIds.has(shiftPattern.id));
  },[allShiftPatterns, allWorkCenters.length, reducedAllWorkCenters, selectedWorkCenterIds, wcToShiftPatternMap]);

  const reducedManagers = useMemo(() => {
    // If no location filter is active, return all work centers.
    if (selectedWorkCenterIds.size === 0 && reducedAllWorkCenters.length === allWorkCenters.length) {
      return allManagers;
    }

    let tempSelectedWorkCenterIds = selectedWorkCenterIds;
    if (tempSelectedWorkCenterIds.size === 0) {
      tempSelectedWorkCenterIds = new Set(reducedAllWorkCenters.map((wc) => wc.id));
    }

    const allowedManagersIds = new Set();
    // For each selected work center, add its corresponding manager IDs to a set.
    for (const wcId of tempSelectedWorkCenterIds) {
      const managerIds = wcToManagerMap.get(wcId);
      if (managerIds) {
        for (const managerId of managerIds) {
          allowedManagersIds.add(managerId);
        }
      }
    }

    // Return only the manager whose IDs are in the allowed set.
    return allManagers.filter((manager) => allowedManagersIds.has(manager.id));
  },[allManagers, allWorkCenters.length, reducedAllWorkCenters, selectedWorkCenterIds, wcToManagerMap]);

	useEffect(() => {
		const searchJobs = (text) => {
			let jobs = Object.values(reducedAllJobs);
			let result = [
				...jobs.filter((x) =>
					x.name.toLowerCase().includes(text?.trimStart("").toLowerCase())
				),
			];
			if (result.length > 0) {
				setSearchedJobs(result);
			} else {
				setSearchedJobs(jobs);
			}
		};

		if (searchedJob !== "") {
			searchJobs(searchedJob);
		} else {
			setSearchedJobs(Object.values(reducedAllJobs));
			setFilteredJobs(Object.values(reducedAllJobs));
		}
	}, [searchedJob, reducedAllJobs]);

	useEffect(() => {
		const searchManager = (text) => {
			let result = [
				...reducedManagers.filter((x) =>
					x.name.toLowerCase().includes(text?.trimStart("").toLowerCase())
				),
			];
			if (result.length > 0) {
				setFilteredManagers(result);
			} else {
				setFilteredManagers(reducedManagers);
			}
		};
		if (searchedManager !== "") {
			searchManager(searchedManager);
		} else {
			setFilteredManagers(reducedManagers);
		}
	}, [searchedManager, reducedManagers]);

	useEffect(() => {
		const searchLocation = (text) => {
			let result = [
				...allLocations.filter((x) =>
					x.name.toLowerCase().includes(text?.trimStart("").toLowerCase())
				),
			];
			if (result.length > 0) {
				setSearchedLocations(result);
			} else {
				setSearchedLocations(allLocations);
			}
		};
		if (searchedLocation !== "") {
			searchLocation(searchedLocation);
		} else {
			setSearchedLocations(allLocations);
			setFilteredLocations(allLocations);
		}
	}, [searchedLocation, allLocations]);

	useEffect(() => {
		const searchShiftPattern = (text) => {
			let result = [
				...reducedShiftPatterns.filter((x) =>
					x.name.toLowerCase().includes(text?.trimStart("").toLowerCase())
				),
			];
			if (result.length > 0) {
				setSearchedShiftPatterns(result);
			} else {
				setSearchedShiftPatterns(reducedShiftPatterns);
			}
		};
		if (searchedShiftPattern !== "") {
			searchShiftPattern(searchedShiftPattern);
		} else {
			setSearchedShiftPatterns(reducedShiftPatterns);
			setFilteredShiftPatterns(reducedShiftPatterns);
		}
	}, [searchedShiftPattern, reducedShiftPatterns]);

	useEffect(() => {
		const searchWorkcenter = (text) => {
			let result = [
				...reducedAllWorkCenters.filter((x) =>
					x.name.toLowerCase().includes(text?.trimStart("").toLowerCase())
				),
			];
			if (result.length > 0) {
				setSearchedWorkcenters(result);
			} else {
				setSearchedWorkcenters(reducedAllWorkCenters);
			}
		};
		if (searchedWorkcenter !== "") {
			searchWorkcenter(searchedWorkcenter);
		} else {
			setSearchedWorkcenters(reducedAllWorkCenters);
			setFilteredWorkcenters(reducedAllWorkCenters);
		}
	}, [searchedWorkcenter, reducedAllWorkCenters]);

	useEffect(() => {
		const searchSkills = (text) => {
			let result = [
				...reducedAllSkills.filter((x) =>
					x.name.toLowerCase().includes(text?.trimStart("").toLowerCase())
				),
			];
			if (result.length > 0) {
				setSearchedSkills(result);
			} else {
				setSearchedSkills(reducedAllSkills);
			}
		};
		if (searchedSkill !== "") {
			searchSkills(searchedSkill);
		} else {
			setSearchedSkills(reducedAllSkills);
			setFilteredSkills(reducedAllSkills);
		}
		if (skillListRef.current) {
			// Re-render after search
			skillListRef.current.resetAfterIndex(0);
		}
	}, [searchedSkill, reducedAllSkills]);

	const resetFilters = (filtersToReset = []) => {
		try {
			if (filtersToReset.length > 0) {
				let newFilters = [...filters];
				filtersToReset.forEach((type) => {
					const index = newFilters.findIndex((obj) => obj.type === type);
					newFilters[index][`${type}s`] = [];
					if (type === "workerType") {
						const workerSubTypeIndex = newFilters.findIndex(
							(obj) => obj.type === "workerSubType"
						);
						newFilters[workerSubTypeIndex].workerSubTypes = [];
					}
				});
				changeFilters(newFilters);
			} else {
				changeFilters([
					{
						type: "location",
						locations: [],
					},
					{
						type: "job",
						jobs: [],
					},
					{
						type: "skill",
						skills: [],
					},
					{
						type: "workerType",
						workerTypes: [],
						workerSubTypes: [],
					},
					{
						type: "workerSubType",
						workerSubTypes: [],
					},
					{
						type: "skillPriority",
						skillPrioritys: [],
					},

					{
						type: "department",
						departments: [],
					},
					{
						type: "manager",
						managers: [],
					},
					{
						type: "workCenter",
						workCenters: [],
					},
					{
						type: "shiftPattern",
						shiftPatterns: [],
					},
				]);
			}

			setExpandedSkills([]);
			setSearchedSkill("");
			setSearchedJob("");
			setSearchedManager("");
			setSearchedDepartment("");
			setSearchedWorkcenter("");
			setSearchedShiftPattern("");
			setSearchedLocation("");
			setRowHeightsForSkill({});
			skillListRef.current.resetAfterIndex(0);
		} catch (e) {
			// Do nothing
		}
	};

	const applyFilter = (type, isAdding, id, levelId = "", name = "") => {
		// Default values
		let jobObject = { ...jobsFilterObject };
		let locationObject = { ...locationsFilterObject };
		let skillObject = { ...skillsFilterObject };
		let workerTypeObject = { ...workerTypeFilterObject };
		let workerSubTypeObject = { ...workerSubTypeFilterObject };
		let skillPriorityObject = { ...skillPriorityFilterObject };
		let departmentObject = { ...departmentFilterObject };
		let managerObject = { ...managerFilterObject };

		let workCenterObject = {
			...workCenterFilterObject,
		};
		let shiftPatternObject = { ...shiftPatternFilterObject };
		// Existing values
		filters.forEach((filter) => {
			if (filter.type === "location") {
				locationObject = { ...filter };
			}
			if (filter.type === "job") {
				jobObject = { ...filter };
			}
			if (filter.type === "skill") {
				skillObject = { ...filter };
			}
			if (filter.type === "workerType") {
				workerTypeObject = { ...filter };
			}
			if (filter.type === "workerSubType") {
				workerSubTypeObject = { ...filter };
			}
			if (filter.type === "skillPriority") {
				skillPriorityObject = { ...filter };
			}

			if (filter.type === "department") {
				departmentObject = { ...filter };
			}
			if (filter.type === "manager") {
				managerObject = { ...filter };
			}
			if (filter.type === "workCenter") {
				workCenterObject = { ...filter };
			}
			if (filter.type === "shiftPattern") {
				shiftPatternObject = { ...filter };
			}
		});
		if (isAdding) {
			// For org filter
			let idsToAdd = [id];

			// Adding filter
			switch (type) {
				case "location":
					// Remove if location exists
					locationObject = {
						...locationObject,
						locations: locationObject.locations.filter((x) => x.id !== id),
					};
					// Add location
					locationObject.locations.push({
						id,
						name,
						timeAdded: new Date().getTime(),
					});
					break;
				case "job":
					// Remove if job exists
					jobObject = {
						...jobObject,
						jobs: jobObject.jobs.filter((x) => x.id !== id),
					};
					// Add job
					jobObject.jobs.push({ id, name, timeAdded: new Date().getTime() });
					break;
				case "skill":
					// Add skill
					if (levelId !== "") {
						let skillByIdArray = skillObject.skills.filter((x) => x.id === id);
						let skillObjectById = { id: id, levelIds: [] };
						if (skillByIdArray.length > 0) {
							skillObjectById = skillByIdArray[0];
							// Remove existing same levelId if any
							skillObjectById.levelIds = skillObjectById.levelIds.filter(
								(x) => x !== levelId
							);
						}
						// Add new levelId
						skillObjectById.levelIds.push(levelId);
						// Find index of skill object to update
						const skillIndex = skillObject.skills.findIndex(
							(el) => parseInt(el.id) === parseInt(id)
						);
						// Update levelIds
						skillObject.skills[skillIndex].levelIds = skillObjectById.levelIds;
					} else {
						skillObject.skills.push({
							id: id,
							name: name,
							levelIds: [],
							timeAdded: new Date().getTime(),
						});
					}
					break;
				case "workerType":
					// Remove if worker type exists
					workerTypeObject = {
						...workerTypeObject,
						workerTypes: workerTypeObject.workerTypes.filter(
							(x) => x.id !== id
						),
					};
					// Add worker type
					workerTypeObject.workerTypes.push({
						id: id,
						text: name,
						timeAdded: new Date().getTime(),
					});
					break;
				case "workerSubType":
					// Remove if worker sub type exists
					workerSubTypeObject = {
						...workerSubTypeObject,
						workerSubTypes: workerSubTypeObject.workerSubTypes.filter(
							(x) => x.id !== id
						),
					};
					// Add worker sub type
					workerSubTypeObject.workerSubTypes.push({
						id: id,
						text: name,
						timeAdded: new Date().getTime(),
					});
					break;
				case "skillPriority":
					skillPriorityObject = {
						...skillPriorityObject,
						skillPrioritys: skillPriorityObject.skillPrioritys.filter(
							(x) => x.id !== id
						),
					};
					skillPriorityObject.skillPrioritys.push({
						id: id,
						text: name,
						timeAdded: new Date().getTime(),
					});
					break;
				case "department":
					// Remove department if exists
					departmentObject = {
						...departmentObject,
						departments: departmentObject.departments.filter(
							(x) => x.id !== id
						),
					};
					// Add department id
					departmentObject.departments.push({
						id,
						timeAdded: new Date().getTime(),
					});
					break;
				case "manager":
					// Remove manager if exists
					managerObject = {
						...managerObject,
						managers: managerObject.managers.filter((x) => x.id !== id),
					};
					// Add manager id
					managerObject.managers.push({
						id,
						name,
						timeAdded: new Date().getTime(),
					});
					break;

				case "workCenter":
					// Remove work center if exists
					workCenterObject = {
						...workCenterObject,
						workCenters: workCenterObject.workCenters.filter(
							(x) => x.id !== id
						),
					};
					// Add work center id
					workCenterObject.workCenters.push({
						id,
						name,
						timeAdded: new Date().getTime(),
					});
					break;

				case "shiftPattern":
					// Remove shift pattern if exists
					shiftPatternObject = {
						...shiftPatternObject,
						shiftPatterns: shiftPatternObject.shiftPatterns.filter(
							(x) => x.id !== id
						),
					};
					// Add shift pattern id
					shiftPatternObject.shiftPatterns.push({
						id,
						name,
						timeAdded: new Date().getTime(),
					});
					break;
				default:
					break;
			}
		} else {
			// Removing filter
			switch (type) {
				case "location":
					// Remove location
					locationObject = {
						...locationObject,
						locations: locationObject.locations.filter((x) => x.id !== id),
					};
					break;
				case "job":
					// Remove job
					jobObject = {
						...jobObject,
						jobs: jobObject.jobs.filter((x) => x.id !== id),
					};
					break;
				case "skill":
					if (levelId !== "") {
						// If level removed
						let skillByIdArray = skillObject.skills.filter((x) => x.id === id);
						let skillObjectById = { id: id, levelIds: [] };
						if (skillByIdArray.length > 0) {
							skillObjectById = skillByIdArray[0];
							skillObjectById.levelIds = skillObjectById.levelIds.filter(
								(x) => x !== levelId
							);
						}
						// Remove existing skill
						skillObject = {
							...skillObject,
							skills: skillObject.skills.filter((x) => x.id !== id),
						};
						// Push skill with updated levelIds
						skillObject.skills.push({
							id: id,
							name: name,
							levelIds: skillObjectById.levelIds,
							timeAdded: new Date().getTime(),
						});
					} else {
						skillObject = {
							...skillObject,
							skills: skillObject.skills.filter((x) => x.id !== id),
						};
					}
					break;
				case "workerType":
					// Remove worker type
					workerTypeObject = {
						...workerTypeObject,
						workerTypes: workerTypeObject.workerTypes.filter(
							(x) => x.id !== id
						),
					};
					if (workerSubTypeObject.workerSubTypes.length > 0) {
						workerSubTypeObject = {
							...workerSubTypeObject,
							workerSubTypes: [],
						};
					}
					break;
				case "workerSubType":
					// Remove worker sub type
					workerSubTypeObject = {
						...workerSubTypeObject,
						workerSubTypes: workerSubTypeObject.workerSubTypes.filter(
							(x) => x.id !== id
						),
					};
					break;
				case "skillPriority":
					// Remove skill priority
					skillPriorityObject = {
						...skillPriorityObject,
						skillPrioritys: skillPriorityObject.skillPrioritys.filter(
							(x) => x.id !== id
						),
					};
					break;
				case "department":
					// Remove department
					departmentObject = {
						...departmentObject,
						departments: departmentObject.departments.filter(
							(x) => x.id !== id
						),
					};
					break;
				case "manager":
					// Remove manager
					managerObject = {
						...managerObject,
						managers: managerObject.managers.filter((x) => x.id !== id),
					};
					break;

				case "workCenter":
					// Remove work center
					workCenterObject = {
						...workCenterObject,
						workCenters: workCenterObject.workCenters.filter(
							(x) => x.id !== id
						),
					};
					break;

				case "shiftPattern":
					// Remove work center
					shiftPatternObject = {
						...shiftPatternObject,
						shiftPatterns: shiftPatternObject.shiftPatterns.filter(
							(x) => x.id !== id
						),
					};
					break;
				default:
					break;
			}
		}
		// Set filters to parent component
		changeFilters([
			locationObject,
			jobObject,
			skillObject,
			workerTypeObject,
			workerSubTypeObject,
			skillPriorityObject,
			departmentObject,
			managerObject,
			workCenterObject,
			shiftPatternObject,
		]);
	};

	const isSkillLevelChecked = (skillId, levelId) => {
		let returnValue = false;
		filters.forEach((filter) => {
			if (filter.type === "skill") {
				filter.skills.forEach((fSkill) => {
					if (fSkill.id === skillId && fSkill.levelIds.includes(levelId)) {
						returnValue = true;
					}
				});
			}
		});
		return returnValue;
	};

	const isSkillChecked = (skillId) => {
		let returnValue = false;
		filters.forEach((filter) => {
			if (filter.type === "skill") {
				filter.skills.forEach((fSkill) => {
					if (fSkill.id === skillId) {
						returnValue = true;
					}
				});
			}
		});
		return returnValue;
	};

	const isJobChecked = (jobId) => {
		let returnValue = false;
		filters.forEach((filter) => {
			if (filter.type === "job") {
				filter.jobs.forEach((fJob) => {
					if (fJob.id === jobId) {
						returnValue = true;
					}
				});
			}
		});
		return returnValue;
	};

	const isLocationChecked = (locationId) => {
		let returnValue = false;
		filters.forEach((filter) => {
			if (filter.type === "location") {
				filter.locations.forEach((fLocation) => {
					if (fLocation.id === locationId) {
						returnValue = true;
					}
				});
			}
		});
		return returnValue;
	};

	const isDepartmentChecked = (departmentId) => {
		let returnValue = false;
		filters.forEach((filter) => {
			if (filter.type === "department") {
				filter.departments.forEach((fDepartment) => {
					if (fDepartment.id === departmentId) {
						returnValue = true;
					}
				});
			}
		});
		return returnValue;
	};

	const isManagerChecked = (managerId) => {
		let returnValue = false;
		filters.forEach((filter) => {
			if (filter.type === "manager") {
				filter.managers.forEach((fManager) => {
					if (fManager.id === managerId) {
						returnValue = true;
					}
				});
			}
		});
		return returnValue;
	};

	const isWorkCenterChecked = (workCenterId) => {
		let returnValue = false;
		filters.forEach((filter) => {
			if (filter.type === "workCenter") {
				filter.workCenters.forEach((fWorkCenter) => {
					if (fWorkCenter.id === workCenterId) {
						returnValue = true;
					}
				});
			}
		});
		return returnValue;
	};

	const isShiftPatternChecked = (shiftPatternId) => {
		let returnValue = false;
		filters.forEach((filter) => {
			if (filter.type === "shiftPattern") {
				filter.shiftPatterns.forEach((fShiftPattern) => {
					if (fShiftPattern.id === shiftPatternId) {
						returnValue = true;
					}
				});
			}
		});
		return returnValue;
	};

	const isWorkerTypeChecked = (workerTypeId) => {
		let returnValue = false;
		filters.forEach((filter) => {
			if (filter.type === "workerType") {
				filter.workerTypes.forEach((fWorkerType) => {
					if (fWorkerType.id === workerTypeId) {
						returnValue = true;
					}
				});
			}
		});
		return returnValue;
	};

	const isWorkerSubTypeChecked = (workerSubTypeId) => {
		let returnValue = false;
		filters.forEach((filter) => {
			if (filter.type === "workerSubType") {
				filter.workerSubTypes.forEach((fWorkerSubType) => {
					if (fWorkerSubType.id === workerSubTypeId) {
						returnValue = true;
					}
				});
			}
		});
		return returnValue;
	};
	const isSkillPriorityChecked = (skillPriorityId) => {
		let returnValue = false;
		filters.forEach((filter) => {
			if (filter.type === "skillPriority") {
				filter.skillPrioritys.forEach((fSkillPriority) => {
					if (fSkillPriority.id === skillPriorityId) {
						returnValue = true;
					}
				});
			}
		});
		return returnValue;
	};

	const expandSkill = (skillId) => {
		let tempExpandedSkills = [...expandedSkills];
		let searchedSkill = tempExpandedSkills.filter((x) => x === skillId);
		if (searchedSkill.length > 0) {
			tempExpandedSkills = tempExpandedSkills.filter((x) => x !== skillId);
		} else {
			tempExpandedSkills.push(skillId);
		}
		setExpandedSkills(tempExpandedSkills);
	};

	const isSkillExpanded = (skillId) => {
		let returnValue = false;
		expandedSkills.forEach((eSkillId) => {
			if (eSkillId === skillId) {
				returnValue = true;
			}
		});
		return returnValue;
	};

	return [
		{
			isSkillLevelChecked,
			isSkillChecked,
			isJobChecked,
			isLocationChecked,
			isWorkerTypeChecked,
			isWorkerSubTypeChecked,
			isSkillPriorityChecked,
			isDepartmentChecked,
			isManagerChecked,
			isWorkCenterChecked,
			isShiftPatternChecked,
			isSkillExpanded,
			skillListRef,
			filterUpdateCount,
			filteredJobs,
			jobsExpanded,
			workcenterExpanded,
			filteredSkills,
			filteredLocations,
			filteredManagers,
			allWorkerTypes,
			allWorkerSubTypes,
			allSkillPriorities,
			allShiftPatterns,
			skillsExpanded,
			departmentExpanded,
			locationsExpanded,
			managerExpanded,
			workerTypeExpanded,
			workerSubTypeExpanded,
			skillPriorityExpanded,
			shiftPatternExpanded,
			searchedJob,
			searchedLocation,
			searchedJobs,
			searchedSkill,
			searchedSkills,
			setSearchedWorkcenter,
			setSearchedLocations,
			searchedDepartment,
			searchedManager,
			searchedLocations,
			filteredWorkcenters,
			filteredShiftPatterns,
			searchedWorkcenter,
			searchedShiftPattern,
			searchedWorkcenters,
			searchedShiftPatterns,
			getSkillItemSize,
			getJobItemSize,
			getLocationItemSize,
			getWorkcenterItemSize,
			getShiftPatternItemSize,
			rowHeightsForSkill,
		},
		{
			resetFilters,
			applyFilter,
			expandSkill,
			setJobsExpanded,
			setWorkcenterExpanded,
			setShiftPatternExpanded,
			setSkillsExpanded,
			setDepartmentExpanded,
			setManagerExpanded,
			setWorkerTypeExpanded,
			setWorkerSubTypeExpanded,
			setSkillPriorityExpanded,
			setLocationsExpanded,
			setSearchedJob,
			setSearchedDepartment,
			setSearchedManager,
			setSearchedLocations,
			setSearchedSkill,
			setSearchedWorkcenter,
			setSearchedShiftPattern,
			setSearchedLocation,
			setFilterUpdateCount,
			setRowHeightForSkill,
		},
	];
};

export default useCrossSkillFilterController;
